/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import Grid from '@mui/material/Grid';
import { Button, Dialog, DialogContent, DialogTitle, LinearProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { appendWafSafeFile, appendWafSafeFormField } from '../common/common';
import { editDownloadsEndPoint, uploadDownloadsEndPoint } from '../common/constants';
import { loadFromLocalStorage } from '../common/localStorage';
import logo from '../images/blueLogo.svg';
import icAdd from '../images/ic-add.svg';
import { actionCreators } from '../store/UploadDownloads';
import DragDropDownloads from './DragDropDownloads';
import './styles.css';
import UploadDownloadsSteps from './UploadDownloadsSteps';

interface Props {
    displayName: any
    autosync: any
    status: any
    description: any
    revision: any
    releaseDate: any
    firmwareType: any,
    introductionDate: any
    modelcode: any
    downloadsClassificationModelCodeMapping: any
    comments: any
    fileinstructions: any

    selectedPdf: any
    selectedProductModels: any
    uploadDialogOpen: boolean
    isEdit?: boolean
    originalProductModels: any
    fileName: any
    fileType: any
    fileSize: any
    UpdateFileName: Function
    UpdateFileType: Function
    UpdateFileSize: Function
    isFileChanged: boolean
    UpdateSelectedPdf: Function
    clearAll: Function
    handleUploadDialogClick: Function
    handleRedirection: Function
}
interface State {
    step: number
    isFormSubmitted: boolean
    isUploading: boolean
    isSuccess: boolean
    isFailure: boolean
}
class UploadDownloads extends React.Component<Props, State> {
    state = {
        step: 1,
        isFormSubmitted: false,
        isUploading: false,
        isSuccess: false,
        isFailure: false
    };
    handleClickOpen = () => {
        this.props.handleUploadDialogClick(true);
    };

    handleClose = () => {
        this.props.clearAll();
        this.props.handleUploadDialogClick(false);
        this.setState({ step: 1 });
    };
    redirect = () => {
        this.props.clearAll();
        this.props.handleUploadDialogClick(false);
        this.setState({ step: 1 });
        this.setState({ isSuccess: false });
        this.setState({ isFormSubmitted: false });
        this.props.handleRedirection(event);
    };
    onDrop = async (selectedPdf: any, fileName: any, fileType: any, fileSize: any) => {
        await this.props.UpdateFileName(`${fileName}`);
        await this.props.UpdateFileType(`${fileType}`);
        await this.props.UpdateFileSize(`${fileSize}`);
        this.props.UpdateSelectedPdf(selectedPdf);
    };

    handleSteps = (steps: number) => {
        if (steps) {
            this.setState({ step: steps });
        }
    };

    handleSubmit = async () => {
        const uploadRequest = new FormData();
        appendWafSafeFormField(uploadRequest, 'filename', `${this.props.fileName}`, 'FileNameEncoding');
        uploadRequest.append('filesize', `${this.props.fileSize}`);
        appendWafSafeFormField(
            uploadRequest,
            'displayname',
            this.props.displayName === undefined || this.props.displayName === null ? '' : `${this.props.displayName}`,
            'DisplayNameEncoding');
        if (!this.props.isEdit || this.props.isFileChanged) {
            appendWafSafeFile(uploadRequest, 'formfile', this.props.selectedPdf, `${this.props.fileName}`);
        }
        uploadRequest.append('status', this.props.status);
        uploadRequest.append('autosync', this.props.autosync);
        appendWafSafeFormField(
            uploadRequest,
            'description',
            this.props.description === undefined || this.props.description === null ? '' : this.props.description,
            'DescriptionEncoding');
        uploadRequest.append('revision', this.props.revision === undefined || this.props.revision === null ? '' : this.props.revision);
        appendWafSafeFormField(uploadRequest, 'releasedate', this.props.releaseDate.toDateString(), 'ReleaseDateEncoding');
        uploadRequest.append('firmwareType', this.props.firmwareType);
        appendWafSafeFormField(
            uploadRequest,
            'introductiondate',
            this.props.introductionDate != null ? this.props.introductionDate.toDateString() : '',
            'IntroductionDateEncoding');
        appendWafSafeFormField(
            uploadRequest,
            'fileinstructions',
            this.props.fileinstructions === undefined || this.props.fileinstructions === null ? '' : this.props.fileinstructions,
            'FileInstructionsEncoding');
        appendWafSafeFormField(
            uploadRequest,
            'comments',
            this.props.comments === undefined || this.props.comments === null ? '' : this.props.comments,
            'CommentsEncoding');
        appendWafSafeFormField(
            uploadRequest,
            'downloadsclassificationmodelcodeMapping',
            JSON.stringify(this.props.downloadsClassificationModelCodeMapping),
            'DownloadsClassificationMappingEncoding');
        const isEditApi: boolean = this.props.isEdit || this.props.isFileChanged;
        if (isEditApi) {
            const addedModels: any = this.props.selectedProductModels.filter((x: any) => {
                return this.props.originalProductModels.indexOf(x) < 0;
            });
            const removedModels: any = this.props.originalProductModels.filter((x: any) => {
                return this.props.selectedProductModels.indexOf(x) < 0;
            });
            appendWafSafeFormField(
                uploadRequest,
                'addedtwodmodelidlist',
                JSON.stringify(addedModels),
                'AddedTwodModelIdListEncoding');
            appendWafSafeFormField(
                uploadRequest,
                'removedtwodmodelidlist',
                JSON.stringify(removedModels),
                'RemovedTwodModelIdListEncoding');
            uploadRequest.append('isfilechanged', JSON.stringify(this.props.isFileChanged));
        }
        else {
            appendWafSafeFormField(
                uploadRequest,
                'twodmodelidlist',
                JSON.stringify(this.props.selectedProductModels),
                'TwodModelIdListEncoding');
        }
        const token: string = loadFromLocalStorage('AuthToken');
        const encodedToken = 'Bearer ' + token;
        const headerDict: any = {
            'Authorization': encodedToken,
        };

        this.setState({ isFormSubmitted: true, isUploading: true });
        const response = await fetch(isEditApi ? editDownloadsEndPoint : uploadDownloadsEndPoint, {
            headers: new Headers(headerDict),
            body: uploadRequest,
            method: 'POST'
        });
        this.setState({ isUploading: false });
        const responseData = response.status;
        if (response.status === 201 || response.status === 200) {
            this.setState({ isSuccess: true }, () => {
                this.redirect();
            });
        }
        else {
            this.setState({ isFailure: true }, () => {
                setTimeout(() => {
                    this.setState({ isFormSubmitted: false, isFailure: false, step: 1 });
                }, 5000);
            });
        }
    };

    render() {
        const { isFormSubmitted, isUploading, isSuccess, isFailure } = this.state;
        const { isEdit, uploadDialogOpen } = this.props;
        return (
            <div>
                {!isEdit && <Button id="upload-button" onClick={this.handleClickOpen}>
                    <Grid container justifyContent="space-evenly">
                        <img src={icAdd}
                            className="ic_add" />
                        <span className="uploadButtonText">Upload</span>
                    </Grid>
                </Button>
                }
                <Dialog
                    fullScreen
                    className="UploadDialog"
                    open={uploadDialogOpen}
                    onClose={this.handleClose}
                >
                    <DialogTitle className="dialogTitle">
                        <Grid container justifyContent="space-between">

                            <span>
                                <img src={logo}
                                />
                                <span className="boldFont uploadTitle">{isEdit ? 'Assign Downloads' : 'Downloads Upload'}</span>
                            </span>
                            {(!isUploading) &&
                                <CloseIcon onClick={this.handleClose} style={{ cursor: 'pointer' }} />}
                        </Grid>
                    </DialogTitle>
                    <DialogContent className="uploadDialogContent">
                        {!isFormSubmitted &&
                            <Grid container className="uploadGrid">
                                {(this.state.step === 1 || this.state.step === 2) &&
                                    < Grid className="dragdrop">
                                        <DragDropDownloads onDrop={this.onDrop} selectedPdf={this.props.selectedPdf} fileName={this.props.fileName}
                                            fileType={this.props.fileType} fileSize={this.props.fileSize} />
                                    </Grid>
                                }

                                <UploadDownloadsSteps handleSubmit={this.handleSubmit} handleSteps={this.handleSteps} isEdit={isEdit} />
                            </Grid>
                        }

                        {isUploading &&
                            <Grid className="uploadDialogMessages">
                                <Grid container justifyContent="center">
                                    Uploading...
                                </Grid>
                                <Grid>
                                    <LinearProgress className="uploadprogress" />
                                </Grid>
                            </Grid>
                        }
                        {isSuccess &&
                            <Grid>
                                <Grid container justifyContent="center">
                                    <Grid>
                                        <CheckCircleOutlineIcon className="uploadSuccess" />
                                    </Grid>
                                </Grid>
                                <Grid container justifyContent="center">
                                    Upload Successful. Redirecting...
                                </Grid>
                            </Grid>
                        }
                        {isFailure &&
                            <Grid className="uploadDialogMessages" container justifyContent="center">
                                There was an error while uploading. Please try again later...
                            </Grid>
                        }

                    </DialogContent>
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    state: state.uploadDownloads,
    displayName: state.uploadDownloads.displayName,
    status: state.uploadDownloads.status,
    autosync: state.uploadDownloads.autosync,
    description: state.uploadDownloads.description,
    revision: state.uploadDownloads.revision,
    releaseDate: state.uploadDownloads.releaseDate,
    introductionDate: state.uploadDownloads.introductionDate,
    firmwareType: state.uploadDownloads.firmwareType,
    modelcode: state.uploadDownloads.modelcode,
    downloadsClassificationModelCodeMapping: state.uploadDownloads.downloadsClassificationModelCodeMapping,
    fileinstructions: state.uploadDownloads.fileinstructions,
    comments: state.uploadDownloads.comments,
    selectedPdf: state.uploadDownloads.selectedPdf,
    selectedProductModels: state.uploadDownloads.selectedProductModels,
    uploadDialogOpen: state.uploadDownloads.uploadDialogOpen,
    originalProductModels: state.uploadDownloads.originalProductModels,
    fileName: state.uploadDownloads.fileName,
    fileType: state.uploadDownloads.fileType,
    fileSize: state.uploadDownloads.fileSize,
    isFileChanged: state.uploadDownloads.isFileChanged,
    isEdit: state.uploadDownloads.isEdit
});

const mapDispatchtoProps = (dispatch: any) =>
    bindActionCreators(actionCreators, dispatch);

export default connect(
    mapStateToProps, mapDispatchtoProps
)(UploadDownloads);
